import { Box, SxProps, Theme, Typography } from '@mui/material';

import { useTranslatedText } from 'hooks/useTranslatedText';
import {
  getSubTicketIcon,
  TransferTicket as TransferTicketType,
} from 'restful/tickets';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import SeatingInfoGroup from 'components/@tickets/SeatingInfoGroup';
import TicketQuantityIcon from 'components/@tickets/TicketQuantityIcon';

interface Props {
  ticket: TransferTicketType;
  sx?: SxProps<Theme>;
}

const TransferTicket = ({ ticket, sx }: Props) => {
  const translate = useTranslatedText();

  const seatingInfo = ticket.bookedInfo;
  const groupedTickets = ticket.children ?? [];
  const hasExtraInfo = !!seatingInfo || groupedTickets.length > 0;

  return (
    <BorderStack sx={{ p: 0, ...sx }}>
      <Box
        sx={{
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: hasExtraInfo ? 0 : 16,
          borderBottomRightRadius: hasExtraInfo ? 0 : 16,
          display: 'flex',
          gap: 2,
          py: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
          alignItems: 'center',
        }}
      >
        <TicketQuantityIcon />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="small"
            sx={{
              fontWeight: 'bold',
              color: 'grey.200',
            }}
          >
            {translate(ticket.name)}
          </Typography>
        </Box>
      </Box>
      <SeatingInfoGroup
        bookObjectsInfo={seatingInfo}
        hasGroupedTickets={groupedTickets.length > 0}
      />
      {groupedTickets.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            borderTop: '1px solid',
            py: { xs: 0.5, md: 1 },
            borderColor: 'grey.800',
            flexDirection: 'column',
            width: '100%',
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
          }}
        >
          {groupedTickets.map((child) => {
            const icon = getSubTicketIcon(child);
            return (
              <Box
                key={child.id}
                sx={{
                  minHeight: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  py: 1,
                  px: 2,
                }}
              >
                {icon ? (
                  <Icon
                    name={icon}
                    width={16}
                    height={16}
                    sx={{ mr: 1, mt: -0.5 }}
                  />
                ) : null}
                <Typography
                  variant="small"
                  sx={{
                    color: 'grey.200',
                    flexGrow: 1,
                    display: 'flex',
                  }}
                >
                  {translate(child.name)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </BorderStack>
  );
};

export default TransferTicket;
